import memoize from 'lodash.memoize';
import { createSelector } from 'reselect';
import {
  complaintsByProperty as complaintsByPropertySelector,
  observedPlatforms as observablePlatformsSelector,
  platformIcons as platformIconsSelector,
  propertyList as propertyListSelector,
  stats as statsSelector,
} from '@/data/data-selectors';
import { propertyListFilter } from '@/filter/property-list-filter';
import { mapFilterParams as mapFilterParamsSelector } from '@/map/map-selectors';
import { PROPERTY_DATA_FILTER_KEYS, PROPERTY_DATA_KV_MAP } from '../constants';
import { getPlatformFriendlyName } from '../utils/string-utils';

export const filteredPropertyList = createSelector(
  propertyListSelector,
  complaintsByPropertySelector,
  mapFilterParamsSelector,
  statsSelector,
  (propertyList, complaintsByProperty, mapFilterParams, stats) =>
    propertyListFilter({
      propertyList,
      complaintsByProperty,
      mapFilterParams,
      stats,
    })
);

export const sortedAndFilteredList = createSelector(
  filteredPropertyList,
  // cache the list so that result is returned if type and order do not change
  (filteredList) =>
    memoize((type, order) => {
      // responsible for toggling ascending or descending order
      const multiplier = order === 'desc' ? -1 : 1;
      // do not mutate filtered list order
      const listToSort = [...filteredList];
      return listToSort.sort(
        (property1, property2) =>
          // If any null is compared to non null, return 1 for non-null
          (property1[type] === null) - (property2[type] === null) ||
          // if p1 is greater than p2 return 1 else -1
          (property1[type] > property2[type] ? 1 : -1) * multiplier
      );
    })
);

export const filterSectionTagsMap = createSelector(
  statsSelector,
  observablePlatformsSelector,
  platformIconsSelector,
  (stats, platforms, platformIcons) => {
    const rentalTypeTags = Object.entries(stats.rentalscape.rentalTypes).reduce(
      (acc, [key, label]) => {
        acc[key] = `Rental Type - ${label}`;
        return acc;
      },
      {}
    );

    return {
      residentialType: {
        primary: 'Residential Type - Primary',
        secondaryInvestment: 'Residential Type - Secondary/Investment',
        unknown: 'Residential Type - Unknown',
      },
      addressIdentification: {
        identifiedListing: 'Address Identification - Identified',
        unidentifiedListing: 'Address Identification - Unidentified',
      },
      locationType: {
        within: 'Property Location - Within Boundary',
        'self-reported': 'Property Location - Self Reported',
        neighboring: 'Property Location - Neighboring',
      },
      unitType: {
        selfContained: 'Unit Type - Full Unit',
        room: 'Unit Type - Partial',
        unknown: 'Unit Type - Unknown',
        other: 'Unit Type - Other',
      },
      rentalStructure: {
        adu: 'Rental Structure - ADU',
        multiFamily: 'Rental Structure - Multi Family',
        unknown: 'Rental Structure - Unknown',
        mainStructure: 'Rental Structure - Main Structure',
      },
      onlinePlatforms: {
        other: 'Online Platform - Other',
        none: 'Online Platform - None',
        ...platforms.reduce(
          (acc, platformId) => ({
            ...acc,
            [platformId]: `Online Platform - ${getPlatformFriendlyName(
              platformId,
              platformIcons
            )}`,
          }),
          {}
        ),
      },
      occupancy: {
        withUnknown: 'Occupancy - Unknown',
      },
      rentalType: rentalTypeTags,
      strLicense: {
        hasStrLicense: 'STR License - Licensed',
        hasNoStrLicense: 'STR License - No License',
      },
      activity: {
        hasBookings: 'Activity - Has Bookings',
        hasAvailability: 'Activity - Has Availability',
        isBlocked: 'Activity - Blocked',
      },
      verification: {
        isVerified: 'Verification - Verified Properties',
        isUnverified: 'Verification - Unverified Properties',
      },
      [PROPERTY_DATA_FILTER_KEYS.complaints]: {
        [PROPERTY_DATA_KV_MAP.complaints.hasComplaints]:
          'Complaints - Has Complaints',
        [PROPERTY_DATA_KV_MAP.complaints.hasNoComplaints]:
          'Complaints - No Complaints',
      },
      [PROPERTY_DATA_FILTER_KEYS.violations]: {
        [PROPERTY_DATA_KV_MAP.violations.hasViolations]:
          'Violations - Has Violations',
        [PROPERTY_DATA_KV_MAP.violations.hasNoViolations]:
          'Violations - No Violations',
      },
      [PROPERTY_DATA_FILTER_KEYS.hoe]: {
        [PROPERTY_DATA_KV_MAP.hoe.claimingHoe]: 'HOE - Claiming HOE',
        [PROPERTY_DATA_KV_MAP.hoe.notClaimingHoe]: 'HOE - Not Claiming HOE',
      },
    };
  }
);
